import React, { useContext, useEffect, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon, EyeIcon, PencilIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Link, useLocation, useParams } from 'wouter';
import Swal from 'sweetalert2';

import {
    addHandlerToOrganizationalUnit,
    addMemberToOrganizationalUnit,
    deleteOrganizationalUnit,
    getOrganizationalUnit,
    removeEmployeeFromOrganizationalUnit,
} from '../services/api_org_unit';
import {
    getJustificationProfiles,
    getOrganizationalUnitJustificationProfile,
    updateOrganizationalUnitJustificationProfile,
    deleteOrganizationalUnitJustificationProfile,
} from '../services/api_justification_profiles';
import { getEmployeesWithActiveContract } from '../services/api_calendar';
import { addOusPermission, getOusPermissions, removeOusPermission } from '../services/api_permissions';

import { AuthContext } from '../contexts/auth';
import Fetching from '../components/fetching';
import Footer from '../components/footer';
import OrgAddEmployeeRoleHandlers from '../components/org_unit_add_handlers';
import OrgAddEmployeeRoleMembers from '../components/org_unit_add_members';
import PermissionsTable from '../components/permessi_table';
import PopoverComponentCustom from '../components/popover_component';
import JustificationProfileListBox from '../components/justification_profile_listbox';


export default function DetailOrgUnit() {
    const { ou_id } = useParams();
    const { token, profile } = useContext(AuthContext);
    const [, setLocation] = useLocation();

    const [unitData, setUnitData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [isVisibleHandlers, setIsVisibleHandlers] = useState(false);
    const [isVisibleMembers, setIsVisibleMembers] = useState(false);
    const [selectedEmployeesHandlers, setSelectedEmployeesHandlers] = useState([]);
    const [selectedEmployeesMembers, setSelectedEmployeesMembers] = useState([]);
    const [dipendenti, setDipendenti] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [parentUnitData, setParentUnitData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [ouPermissions, setOuPermissions] = useState([]);
    const [showPermissionsTable, setShowPermissionsTable] = useState(false);
    const errorRef = useRef(null);

    const [justificationProfile, setJustificationProfile] = useState(null);
    const [justificationProfiles, setJustificationProfiles] = useState([]);
    const [isLoadingJustificationProfile, setIsLoadingJustificationProfile] = useState(false);
    const [isEditingJustificationProfile, setIsEditingJustificationProfile] = useState(false);
    const [isSavingJustificationProfile, setIsSavingJustificationProfile] = useState(false);
    const [showJustificationDetails, setShowJustificationDetails] = useState(false);
    const [selectedJustificationProfile, setSelectedJustificationProfile] = useState(null);

    const canManageJustifications =
        profile.customer_type !== 'employee' ||
        profile.employee?.permissions?.includes('hr.manage_justification_all') ||
        profile.employee?.permissions?.includes('hr.manage_justification_ou');

    const isEmployeeJustificationManager =
        profile.customer_type === 'employee' &&
        (
            profile.employee?.permissions?.includes('hr.manage_justification_all') ||
            profile.employee?.permissions?.includes('hr.manage_justification_ou')
        );
        
    useEffect(() => {
        setIsLoading(true);
        setIsError(false);
        getOrganizationalUnit(token, ou_id).then((data) => setUnitData(data.data))
        .catch((error) => {
            console.error('Error fetching units:', error);
            setIsError(true);
        })
        .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        if (!unitData?.parent) {
            setParentUnitData(null);
            return;
        }

        getOrganizationalUnit(token, unitData.parent)
            .then((data) => setParentUnitData(data.data))
            .catch((error) => console.error('Error fetching parent unit:', error));
    }, [unitData.parent]);

    useEffect(() => {
        const fetchPermissions = async () => {
            try {
                const activePermissions = await getOusPermissions(token, ou_id);
                setOuPermissions(activePermissions);
            } catch (error) {
                console.error('Error fetching organizational unit permissions:', error);
                setIsError(true);
            } finally {
                setIsLoading(false);
            }
        };
    
        if (ou_id && profile.customer_type !== 'employee') {
            setIsLoading(true);
            fetchPermissions();
        }
    }, [ou_id, token, profile.customer_type]);

    useEffect(() => {
        const fetchJustificationProfileData = async () => {
            try {
                setIsLoadingJustificationProfile(true);

                const [assignmentResponse, profilesResponse] = await Promise.all([
                    getOrganizationalUnitJustificationProfile(token, ou_id),
                    getJustificationProfiles(token),
                ]);

                setJustificationProfile(assignmentResponse?.profile || null);
                setJustificationProfiles(
                    Array.isArray(profilesResponse) ? profilesResponse : profilesResponse?.results || []
                );
            } catch (error) {
                console.error('Errore durante il recupero dei profili giustificativi:', error);
            } finally {
                setIsLoadingJustificationProfile(false);
            }
        };

        if (token && ou_id && canManageJustifications) fetchJustificationProfileData();
    }, [token, ou_id, profile.customer_type]);

    const availableEmployees = Array.isArray(dipendenti)
        ? dipendenti.filter((employee) =>
            (!unitData.handlers || !unitData.handlers.find((handler) => handler.id === employee.id)) &&
            (!unitData.members || !unitData.members.find((member) => member.id === employee.id))
        )
        : [];

    const handleDeleteOu = async () => {
        const callback = async () => {
            try {
                await deleteOrganizationalUnit(token, ou_id);
                await Swal.fire({
                    title: 'Unità Organizzativa eliminata con successo!',
                    icon: 'success',
                });
                setLocation('/organization');
            } catch (error) {
                console.error('Errore durante l\'eliminazione dell\'unità organizzativa:', error);
                Swal.fire({
                    text: 'Errore durante l\'eliminazione dell\'unità organizzativa',
                    icon: 'error',
                });
            }
        };

        const result = await Swal.fire({
            icon: 'question',
            text: unitData.is_parent
                ? `Sei sicuro di voler procedere con l'eliminazione dell'Unità Organizzativa ${unitData.name.toUpperCase()}? Verranno eliminate anche le unità ad essa collegate.`
                : `Sei sicuro di voler eliminare l'Unità Organizzativa ${unitData.name.toUpperCase()}?`,
            showCancelButton: true,
            confirmButtonText: 'Sì, elimina',
            cancelButtonColor: '#d1d5db',
            cancelButtonText: 'No',
        });

        if (result.isConfirmed) callback();
    };

    const toggleVisibilityHandlers = () => {
        if (isVisibleMembers) {
            setErrorMessage('Stai aggiungendo i Dipendenti, completa l\'operazione o seleziona "Annulla" per abilitare l\'aggiunta di Gestori');
            setSelectedEmployeesHandlers([]);
            return;
        }

        setIsVisibleHandlers(!isVisibleHandlers);
        setSelectedEmployeesHandlers([]);
        setErrorMessage('');
    };

    const toggleVisibilityMembers = () => {
        if (isVisibleHandlers) {
            setErrorMessage('Stai aggiungendo i Gestori, completa l\'operazione o seleziona "Annulla" per abilitare l\'aggiunta di Dipendenti');
            setSelectedEmployeesMembers([]);
            return;
        }

        setIsVisibleMembers(!isVisibleMembers);
        setSelectedEmployeesMembers([]);
        setErrorMessage('');
    };

    const handleSelectionChangeHandlers = (newSelectedEmployees) => {
        setSelectedEmployeesHandlers(newSelectedEmployees);
        setErrorMessage('');
    };

    const handleSelectionChangeMembers = (newSelectedEmployees) => {
        setSelectedEmployeesMembers(newSelectedEmployees);
        setErrorMessage('');
    };

    const handleUnselectEmployee = (employeeId, listType) => {
        if (listType === 'handlers') {
            setSelectedEmployeesHandlers(selectedEmployeesHandlers.filter((employee) => employee.id !== employeeId));
        } else if (listType === 'members') {
            setSelectedEmployeesMembers(selectedEmployeesMembers.filter((employee) => employee.id !== employeeId));
        }
    };

    const handleSaveHandlers = async (employees) => {
        try {
            setIsSaving(true);

            if (!ou_id) {
                console.error('ou_id è undefined');
                return;
            }

            await Promise.all(employees.map((employee) => addHandlerToOrganizationalUnit(token, ou_id, employee.id)));
            const updatedUnitData = await getOrganizationalUnit(token, ou_id);
            setUnitData(updatedUnitData.data);
            toggleVisibilityHandlers();
            handleSelectionChangeHandlers([]);
        } catch (error) {
            console.error('Errore durante l\'aggiunta dei dipendenti all\'unità organizzativa:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveMembers = async (employees) => {
        try {
            setIsSaving(true);

            if (!ou_id) {
                console.error('ou_id è undefined');
                return;
            }

            await Promise.all(
                employees.map((employee) => addMemberToOrganizationalUnit(token, ou_id, employee.id))
            );

            const updatedUnitData = await getOrganizationalUnit(token, ou_id);
            setUnitData(updatedUnitData.data);
            toggleVisibilityMembers();
            handleSelectionChangeMembers([]);
        } catch (error) {
            console.error('Errore durante l\'aggiunta dei dipendenti all\'unità organizzativa:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleRemoveEmployeeOU = async (employee_id) => {
        try {
            if (!ou_id) {
                console.error('ou_id è undefined');
                return;
            }
    
            await removeEmployeeFromOrganizationalUnit(token, ou_id, employee_id);

            const updatedUnitData = await getOrganizationalUnit(token, ou_id);
            setUnitData(updatedUnitData.data);

            const updatedAvailableEmployees = await getEmployeesWithActiveContract(token);
            setDipendenti(updatedAvailableEmployees);
        } catch (error) {
            console.error('Errore durante la rimozione del dipendente dall\'unità organizzativa:', error);
        }
    };

    useEffect(() => {
        if (errorMessage && errorRef.current) {
            errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [errorMessage]);

    const handleAddPermissions = () => setShowPermissionsTable(true);

    const handlePermissionChange = async (permissionId, isChecked) => {
        try {
            if (isChecked) await addOusPermission(token, ou_id, permissionId);
            else await removeOusPermission(token, ou_id, permissionId);

            const activePermissions = await getOusPermissions(token, ou_id);
            setOuPermissions(activePermissions);
        } catch (error) {
            console.error('Errore durante l\'aggiornamento dei permessi:', error);
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Si è verificato un errore durante l\'aggiornamento dei permessi.',
            });
        }
    };

    const handleJustificationProfileChange = (selectedProfile) => {
        if (!selectedProfile?.id) return;

        if (selectedProfile.id === justificationProfile?.id) {
            setSelectedJustificationProfile(null);
            return;
        }

        setSelectedJustificationProfile(selectedProfile);
    };

    const handleConfirmJustificationProfile = async () => {
        if (!selectedJustificationProfile?.id) return;

        const wasAlreadyAssigned = Boolean(justificationProfile);

        try {
            setIsSavingJustificationProfile(true);

            const response = await updateOrganizationalUnitJustificationProfile(
                token,
                ou_id,
                selectedJustificationProfile.id
            );

            setJustificationProfile(response?.profile || null);
            setSelectedJustificationProfile(null);
            setIsEditingJustificationProfile(false);
            setShowJustificationDetails(false);

            Swal.fire({
                icon: 'success',
                title: wasAlreadyAssigned ? 'Profilo modificato' : 'Profilo assegnato',
                text: wasAlreadyAssigned
                    ? 'Il profilo giustificativi è stato sostituito correttamente'
                    : 'Il profilo giustificativi è stato assegnato correttamente',
                timer: 1800,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error(
                'Errore durante l\'assegnazione del profilo giustificativi:',
                error
            );

            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Non è stato possibile assegnare il profilo giustificativi.',
            });
        } finally {
            setIsSavingJustificationProfile(false);
        }
    };

    const handleDeleteJustificationProfile = async () => {
        if (!justificationProfile) return;

        const result = await Swal.fire({
            icon: 'question',
            title: 'Rimuovi profilo',
            text: `Sei sicuro di voler rimuovere il profilo "${justificationProfile.name}" da questa unità organizzativa?`,
            showCancelButton: true,
            confirmButtonText: 'Sì, rimuovi',
            cancelButtonText: 'Annulla',
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#d1d5db',
        });

        if (!result.isConfirmed) return;

        try {
            setIsSavingJustificationProfile(true);

            await deleteOrganizationalUnitJustificationProfile(token, ou_id);

            setJustificationProfile(null);
            setSelectedJustificationProfile(null);
            setIsEditingJustificationProfile(false);
            setShowJustificationDetails(false);

            Swal.fire({
                icon: 'success',
                title: 'Profilo rimosso',
                text: 'Il profilo giustificativi è stato rimosso dall\'unità organizzativa.',
                timer: 1800,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error('Errore durante la rimozione del profilo giustificativi:', error);

            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Non è stato possibile rimuovere il profilo giustificativi.',
            });
        } finally {
            setIsSavingJustificationProfile(false);
        }
    };

    if (isLoading) return <Fetching />;
    if (isError) return <div>Error loading data</div>;

    return (
        <div className="flex min-h-screen flex-col">
            <PopoverComponentCustom
                title={
                    <>
                        Unità Organizzativa:&nbsp;
                        <span className="uppercase">{unitData.name}</span>
                    </>
                }
            />

            <main className="mt-10 flex-grow px-5 pb-8">
                <div className="container mx-auto m-5 px-5">

                    {/* ACTIONS */}
                    <div className="grid grid-cols-2">
                        <div className="col-span-1">
                            <Link
                                href="/organization"
                                className="mr-2 inline-block rounded-md border bg-grey-400 px-3 py-2 text-center text-sm font-semibold text-grey-800 shadow-sm hover:bg-gray-100"
                            >
                                Torna indietro
                            </Link>
                        </div>

                        <div className="col-span-1 flex justify-end">
                            <Link
                                href={`/organization/${ou_id}/edit`}
                                className="me-3 flex items-center justify-center rounded-md border px-1 font-semibold text-gray-700 shadow-md hover:bg-gray-100"
                                title="Modifica"
                            >
                                <p className="hidden p-2 text-sm sm:block">Modifica</p>
                                <PencilIcon className="h-6 w-6 text-indigo-600" />
                            </Link>

                            <button
                                type="button"
                                onClick={handleDeleteOu}
                                className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
                            >
                                <p className="hidden sm:block">Elimina Unità</p>
                                <TrashIcon className="-mr-0.5 h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    {/* UNITÀ DI APPARTENENZA */}
                    <div className="my-5 rounded-md border border-gray-200 shadow-md">
                        <div className="flex items-center rounded-t-md bg-gray-100 p-3">
                            <h4 className="text-sm font-semibold uppercase">Unità di Appartenenza</h4>
                        </div>
                        <p className="p-3 text-sm">
                            {parentUnitData ? parentUnitData.name : 'Nessuna unità di appartenenza'}
                        </p>
                    </div>

                    {/* PROFILO GIUSTIFICATIVI */}
                    {canManageJustifications && (
                        <div className="relative z-20 my-5 overflow-visible rounded-md border border-gray-200 bg-white shadow-md">
                            <div className="flex items-center justify-between rounded-t-md bg-gray-100 p-3">
                                <div>
                                    <h4 className="text-sm font-semibold uppercase">Profilo giustificativi</h4>

                                    {isEmployeeJustificationManager && (
                                        <p className="mt-1 text-xs font-normal normal-case text-gray-500">
                                            Puoi gestire e modificare l'assegnazione del profilo a questa unità organizzativa,
                                            ma non creare nuovi gruppi. Per la creazione di un nuovo gruppo rivolgiti al gestore dell'account aziendale.
                                        </p>
                                    )}
                                </div>

                                {justificationProfile && !isEditingJustificationProfile && !isLoadingJustificationProfile && (
                                    <div className="flex items-center gap-1">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsEditingJustificationProfile(true);
                                                setSelectedJustificationProfile(null);
                                                setShowJustificationDetails(false);
                                            }}
                                            disabled={isSavingJustificationProfile}
                                            className="rounded-md p-1 text-gray-600 transition-colors hover:bg-white hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
                                            title="Modifica profilo giustificativi"
                                        >
                                            <PencilSquareIcon className="h-6 w-6 shrink-0 text-indigo-700" />
                                        </button>

                                        <button
                                            type="button"
                                            onClick={handleDeleteJustificationProfile}
                                            disabled={isSavingJustificationProfile}
                                            className="rounded-md p-1 text-gray-600 transition-colors hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                                            title="Rimuovi profilo giustificativi"
                                        >
                                            <TrashIcon className="h-6 w-6 shrink-0" />
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="relative overflow-visible p-3">
                                {isLoadingJustificationProfile ? (
                                    <p className="text-sm text-gray-400">Caricamento...</p>
                                ) : justificationProfile && !isEditingJustificationProfile ? (
                                    <>
                                        <div className="flex flex-wrap items-center justify-between gap-3">
                                            <div className="flex min-w-0 items-center gap-2">
                                                <div className="h-2 w-2 shrink-0 rounded-full bg-indigo-600" />
                                                <span className="truncate text-sm font-semibold uppercase text-indigo-800">
                                                    {justificationProfile.name}
                                                </span>
                                                <span className="rounded-md bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">
                                                    {justificationProfile.justifications?.length || 0}{' '}
                                                    {(justificationProfile.justifications?.length || 0) === 1
                                                        ? 'giustificativo'
                                                        : 'giustificativi'}
                                                </span>
                                            </div>

                                            {justificationProfile.justifications?.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => setShowJustificationDetails((prev) => !prev)}
                                                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                                                >
                                                    <EyeIcon className="h-4 w-4" />
                                                    {showJustificationDetails ? 'Nascondi dettagli' : 'Visualizza dettagli'}
                                                    <ChevronDownIcon
                                                        className={`h-4 w-4 transition-transform ${showJustificationDetails ? 'rotate-180' : ''}`}
                                                    />
                                                </button>
                                            )}
                                        </div>

                                        {showJustificationDetails && justificationProfile.justifications?.length > 0 && (
                                            <div className="mt-3 overflow-hidden rounded-md border border-gray-200">
                                                <div className="grid w-full grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
                                                    {justificationProfile.justifications.map((item) => (
                                                        <div
                                                            key={item.id}
                                                            title={item.justification?.description || ''}
                                                            className="
                                                                flex min-w-0 items-center gap-2
                                                                border-b border-r border-gray-200
                                                                px-3 py-1.5
                                                                transition-colors
                                                                hover:bg-indigo-50/50
                                                                sm:[&:nth-child(2n)]:border-r-0
                                                                xl:[&:nth-child(2n)]:border-r
                                                                xl:[&:nth-child(3n)]:border-r-0
                                                            "
                                                        >
                                                            <span className="w-[55px] shrink-0 text-[11px] font-bold uppercase tracking-wide text-indigo-700">
                                                                {item.justification?.code}
                                                            </span>

                                                            <span className="min-w-0 truncate text-sm text-gray-700">
                                                                {item.justification?.description || '—'}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="relative z-[100]">
                                        <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                            {justificationProfile ? 'Sostituisci profilo' : 'Assegna profilo'}
                                        </label>

                                        <div className="flex items-start gap-2">
                                            <div className="relative z-[100] min-w-0 flex-1">
                                                {justificationProfiles.length > 0 ? (
                                                    <JustificationProfileListBox
                                                        profiles={justificationProfiles}
                                                        value={selectedJustificationProfile || justificationProfile}
                                                        onChange={handleJustificationProfileChange}
                                                        disabled={isSavingJustificationProfile}
                                                    />
                                                ) : (
                                                    <div className="flex h-10 items-center rounded-md border border-gray-300 bg-gray-50 px-3 text-sm text-gray-400">
                                                        Non sono presenti Gruppi di Giustificativi. Puoi crearli nella sezione "Configurazione Parametri".
                                                    </div>
                                                )}
                                            </div>

                                            {justificationProfile && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedJustificationProfile(null);
                                                        setIsEditingJustificationProfile(false);
                                                    }}
                                                    disabled={isSavingJustificationProfile}
                                                    className="h-10 shrink-0 rounded-md border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-600 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                                >
                                                    Annulla
                                                </button>
                                            )}
                                        </div>
                                        
                                        {/* PROFILO ATTUALMENTE ASSOCIATO */}
                                        {justificationProfile && !selectedJustificationProfile && (
                                            <div className="mt-4 overflow-hidden rounded-md border border-gray-200 bg-white">
                                                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2.5">
                                                    <div className="min-w-0">
                                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                            Gruppo attualmente associato
                                                        </p>

                                                        <p className="mt-0.5 truncate text-sm font-semibold uppercase text-indigo-800">
                                                            {justificationProfile.name}
                                                        </p>
                                                    </div>

                                                    <span className="rounded-md bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">
                                                        {justificationProfile.justifications?.length || 0}{' '}
                                                        {(justificationProfile.justifications?.length || 0) === 1
                                                            ? 'giustificativo'
                                                            : 'giustificativi'}
                                                    </span>
                                                </div>

                                                {justificationProfile.justifications?.length > 0 ? (
                                                    <div className="grid w-full grid-cols-1 bg-white sm:grid-cols-2 xl:grid-cols-3">
                                                        {justificationProfile.justifications.map((item) => (
                                                            <div
                                                                key={item.id}
                                                                title={item.justification?.description || ''}
                                                                className="
                                                                    flex min-w-0 items-center gap-2
                                                                    border-b border-r border-gray-200
                                                                    px-3 py-1.5
                                                                    transition-colors
                                                                    hover:bg-indigo-50/50
                                                                    sm:[&:nth-child(2n)]:border-r-0
                                                                    xl:[&:nth-child(2n)]:border-r
                                                                    xl:[&:nth-child(3n)]:border-r-0
                                                                "
                                                            >
                                                                <span className="w-[55px] shrink-0 text-[11px] font-bold uppercase tracking-wide text-indigo-700">
                                                                    {item.justification?.code}
                                                                </span>

                                                                <span className="min-w-0 truncate text-sm text-gray-700">
                                                                    {item.justification?.description || '—'}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="bg-white px-3 py-3 text-xs text-gray-500">
                                                        Il gruppo associato non contiene giustificativi
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {selectedJustificationProfile && (
                                            <div className="mt-4 overflow-hidden rounded-md border border-gray-200 bg-gray-50">
                                                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-3 py-2.5">
                                                    <div className="min-w-0">
                                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                            Anteprima gruppo selezionato
                                                        </p>

                                                        <p className="mt-0.5 truncate text-sm font-semibold uppercase text-indigo-800">
                                                            {selectedJustificationProfile.name}
                                                        </p>
                                                    </div>

                                                    <span className="rounded-md bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">
                                                        {selectedJustificationProfile.justifications?.length || 0}{' '}
                                                        {(selectedJustificationProfile.justifications?.length || 0) === 1
                                                            ? 'giustificativo'
                                                            : 'giustificativi'}
                                                    </span>
                                                </div>

                                                {selectedJustificationProfile.justifications?.length > 0 ? (
                                                    <div className="grid w-full grid-cols-1 bg-white sm:grid-cols-2 xl:grid-cols-3">
                                                        {selectedJustificationProfile.justifications.map((item) => (
                                                            <div
                                                                key={item.id}
                                                                title={item.justification?.description || ''}
                                                                className="
                                                                    flex min-w-0 items-center gap-2
                                                                    border-b border-r border-gray-200
                                                                    px-3 py-1.5
                                                                    transition-colors
                                                                    hover:bg-indigo-50/50
                                                                    sm:[&:nth-child(2n)]:border-r-0
                                                                    xl:[&:nth-child(2n)]:border-r
                                                                    xl:[&:nth-child(3n)]:border-r-0
                                                                "
                                                            >
                                                                <span className="w-[55px] shrink-0 text-[11px] font-bold uppercase tracking-wide text-indigo-700">
                                                                    {item.justification?.code}
                                                                </span>

                                                                <span className="min-w-0 truncate text-sm text-gray-700">
                                                                    {item.justification?.description || '—'}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="bg-white px-3 py-3 text-xs text-gray-500">
                                                        Il gruppo selezionato non contiene giustificativi
                                                    </div>
                                                )}

                                                <div className="flex justify-end gap-2 border-t border-gray-200 px-3 py-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => setSelectedJustificationProfile(null)}
                                                        disabled={isSavingJustificationProfile}
                                                        className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-semibold text-gray-600 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                                    >
                                                        Annulla selezione
                                                    </button>

                                                    <button
                                                        type="button"
                                                        onClick={handleConfirmJustificationProfile}
                                                        disabled={isSavingJustificationProfile}
                                                        className="rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                                                    >
                                                        {isSavingJustificationProfile
                                                            ? 'Salvataggio...'
                                                            : justificationProfile
                                                                ? 'Conferma sostituzione'
                                                                : 'Conferma assegnazione'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* PERMESSI UNITÀ ORGANIZZATIVA */}
                    {profile.customer_type !== 'employee' && (
                        <div className="my-5 rounded-md border border-gray-200 shadow-md">
                            <div className="flex items-center bg-gray-100 p-3">
                                <h4 className="me-3 text-sm font-semibold uppercase">
                                    Permessi dell'unità organizzativa
                                </h4>

                                {!showPermissionsTable ? (
                                    <button
                                        type="button"
                                        className="relative inline-flex w-24 items-center justify-center rounded-sm bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500"
                                        onClick={handleAddPermissions}
                                    >
                                        Aggiungi
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="relative inline-flex w-24 items-center justify-center rounded-sm bg-gray-500 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-gray-400"
                                        onClick={() => setShowPermissionsTable(false)}
                                    >
                                        Chiudi
                                    </button>
                                )}
                            </div>

                            {showPermissionsTable ? (
                                <div className="p-4 text-sm">
                                    <PermissionsTable
                                        activePermissions={ouPermissions.map((permission) => permission.id)}
                                        onChange={handlePermissionChange}
                                    />
                                </div>
                            ) : (
                                <div className="p-3 text-sm">
                                    {ouPermissions.length > 0 ? (
                                        <>
                                            <h4 className="mb-2 text-sm font-semibold uppercase text-indigo-800">
                                                Permessi assegnati ai gestori dell'unità
                                            </h4>

                                            <div className="mb-4 grid grid-cols-2 gap-4">
                                                {ouPermissions.filter((permission) => permission.role === 'handler').length > 0 ? (
                                                    ouPermissions
                                                        .filter((permission) => permission.role === 'handler')
                                                        .map((permission) => (
                                                            <div key={permission.id} className="flex items-center text-gray-800">
                                                                <CheckIcon className="mr-2 h-4 w-4 text-indigo-600" />
                                                                {permission.name}
                                                            </div>
                                                        ))
                                                ) : (
                                                    <p>Non sono presenti permessi per i gestori dell'unità</p>
                                                )}
                                            </div>

                                            <h4 className="mb-2 text-sm font-semibold uppercase text-indigo-800">
                                                Permessi assegnati ai dipendenti dell'unità
                                            </h4>

                                            <div className="grid grid-cols-2 gap-4">
                                                {ouPermissions.filter((permission) => permission.role === 'employee').length > 0 ? (
                                                    ouPermissions
                                                        .filter((permission) => permission.role === 'employee')
                                                        .map((permission) => (
                                                            <div key={permission.id} className="flex items-center text-gray-800">
                                                                <CheckIcon className="mr-2 h-4 w-4 text-indigo-600" />
                                                                {permission.name}
                                                            </div>
                                                        ))
                                                ) : (
                                                    <p>Non sono presenti permessi per i dipendenti dell'unità</p>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <p>Nessun permesso attivo per questa unità</p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {errorMessage && (
                        <p ref={errorRef} className="px-3 text-center text-sm text-red-500">
                            {errorMessage}
                        </p>
                    )}

                    <OrgAddEmployeeRoleHandlers
                        unitData={unitData}
                        isVisibleHandlers={isVisibleHandlers}
                        selectedEmployeesHandlers={selectedEmployeesHandlers}
                        handleSelectionChangeHandlers={handleSelectionChangeHandlers}
                        toggleVisibilityHandlers={toggleVisibilityHandlers}
                        handleUnselectEmployee={handleUnselectEmployee}
                        handleSaveHandlers={handleSaveHandlers}
                        handleRemoveEmployeeOU={handleRemoveEmployeeOU}
                        isSaving={isSaving}
                        token={token}
                    />

                    <OrgAddEmployeeRoleMembers
                        unitData={unitData}
                        token={token}
                        isVisibleMembers={isVisibleMembers}
                        selectedEmployeesMembers={selectedEmployeesMembers}
                        handleSelectionChangeMembers={handleSelectionChangeMembers}
                        toggleVisibilityMembers={toggleVisibilityMembers}
                        handleUnselectEmployee={handleUnselectEmployee}
                        handleSaveMembers={handleSaveMembers}
                        handleRemoveEmployeeOU={handleRemoveEmployeeOU}
                        isSaving={isSaving}
                    />

                </div>
            </main>
            <Footer />
        </div>
    );
}